import React, { useRef, useState } from 'react';

const DEFAULT_MAX_SIZE_MB = 10;

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// A file attached this session is represented as a full base64 data URI
// (e.g. "data:application/pdf;base64,...") plus its original name/size, so the
// backend receives a single self-describing string field and doesn't need a
// separate multipart upload endpoint — it just decodes the data: prefix.
// An already-saved record's attachment comes back from the API as a plain
// URL (http/https) or a legacy free-text reference; those render as a link
// with a "Replace" action rather than being re-read into memory.
function isDataUri(value) {
  return typeof value === 'string' && value.startsWith('data:');
}

function isUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

export default function FileUploadField({
  label = 'Attachment',
  required = false,
  value = '',
  fileName = '',
  onChange,
  accept = 'image/*,.pdf',
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  hint,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const [localName, setLocalName] = useState(fileName);

  const handlePick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleFile = (file) => {
    setError('');
    if (!file) return;

    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File is too large — max ${maxSizeMB}MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setLocalName(file.name);
      onChange?.(reader.result, file.name);
    };
    reader.onerror = () => setError('Failed to read the selected file.');
    reader.readAsDataURL(file);
  };

  const handleInputChange = (event) => {
    handleFile(event.target.files?.[0] ?? null);
    event.target.value = '';
  };

  const handleClear = () => {
    setError('');
    setLocalName('');
    onChange?.('', '');
  };

  const displayName = isDataUri(value)
    ? (localName || fileName || 'Attached file')
    : isUrl(value)
      ? (fileName || value.split('/').pop())
      : value;

  return (
    <div className="space-y-1.5">
      <label className="block text-[12px] font-semibold text-gray-700">
        {label}{required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      {value ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3">
          {isUrl(value) ? (
            <a href={value} target="_blank" rel="noreferrer" className="flex-1 truncate text-[13px] text-green-700 hover:underline">
              {displayName}
            </a>
          ) : (
            <span className="flex-1 truncate text-[13px] text-gray-700">{displayName}</span>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-600 hover:bg-gray-100"
            >
              Replace
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={handlePick}
          disabled={disabled}
          className="w-full rounded-xl border border-dashed border-green-300 bg-green-50 px-3.5 py-3 text-[12px] font-semibold text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Upload file — image or PDF, max {maxSizeMB}MB
        </button>
      )}

      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handleInputChange} disabled={disabled} />

      {hint && !error && <p className="text-[11px] text-gray-400">{hint}</p>}
      {error && <p className="text-[11px] text-red-600">{error}</p>}
    </div>
  );
}

export { formatBytes };
